import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, TextField } from '../../../../../components';
import {
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  IconButton
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { PHARMACY_SALE_MODE, RECEIPT_MODE_INV } from '../../../../../constants/dropdown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const initialOpdPatientObj = {
  data: [],
  loading: false
};

const initialIpdPatientObj = {
  data: [],
  loading: false
};

const initialGeneralPatientObj = {
  data: [],
  loading: false
};

const initialReceiptObj = {
  data: [],
  loading: false
};

export const ExpenseEntry = () => {
  const doctorIDRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opdPatientDataObj, setOpdPatientDataObj] = useState(initialOpdPatientObj);
  const [ipdPatientDataObj, setIpdPatientDataObj] = useState(initialIpdPatientObj);
  const [receiptVoucherDataObj, setReceiptVoucherDataObj] = useState(initialReceiptObj);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [OpenQueryDialogs, setOpenQueryDialogs] = useState(false);
  const [ipdInputs, setIpdInputs] = useState('');
  const [opdInputs, setOpdInputs] = useState('');
  const [input, setInput] = useState('');
  const [labDetails, setLabDetails] = useState([]);
  const [serviceDetails, setServiceDetails] = useState([]);
  const [accountLedger, setAccountLedger] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        receipt_mode: Yup.string().required(' '),
        rec_amt: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5)
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue('', ['_id', 'status', 'createdAt']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      const formattedRegDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('patient_name', selectedRecord?.patient_name);
      setValue('uhid', selectedRecord?.uhid);
      setValue('receipt_no', selectedRecord?.receipt_no);
      setValue('mobile', selectedRecord?.mobile);
      setValue('ipd_no', selectedRecord?.ipd_no);
      setValue('rec_amt', selectedRecord?.rec_amt);
      setValue('ledger', selectedRecord?.ledger);
      setValue('receipt_mode', selectedRecord?.receipt_mode);
      setValue('remarks', selectedRecord?.remarks);
      setValue('payable_amt', selectedRecord?.payable_amt);
      setValue('type', selectedRecord?.type);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/patient-receipt-voucher-update' : '/patient-receipt-voucher-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...formValues,
        createdBy: user.name,
        updatedBy: editFlag ? user.name : '',
        organisation
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'Updated' : 'Created'} Patient Receipt Voucher`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Patient Receipt Voucher`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialogs(true);
    setReceiptVoucherDataObj({ loading: true });
    const response = await request('/patient-receipt-voucher-list', { method: 'GET' });
    if (response.success) {
      setReceiptVoucherDataObj({ loading: false, data: response.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setReceiptVoucherDataObj({ loading: false });
    }
    setLoading(false);
  };

  useEffect(() => {
    handleSearch();
  }, [input]);

  const handleSearch = async () => {
    setReceiptVoucherDataObj({ loading: true });
    const query = {};
    const lowercasedInput = input.toLowerCase().trim();
    if (lowercasedInput) {
      query.uhid = lowercasedInput;
    }
    const response = await request('/patient-receipt-voucher-list', { method: 'GET', query: query });
    if (response.success) {
      setReceiptVoucherDataObj({ loading: false, data: response.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setReceiptVoucherDataObj({ loading: false });
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setInput(e.target.value);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/retail-sale-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Retail Sale Pos', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Retail Sale Pos', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/retail-sale-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Retail Sale Pos', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Retail Sale Pos', { variant: 'error' });
    }
    setLoading(false);
  };

  const getAccountLedgerData = useCallback(async () => {
    const data = await dropdownFetch('account-ledger');
    setAccountLedger(data);
  }, []);

  useEffect(() => {
    getAccountLedgerData();
  }, [getAccountLedgerData]);

  useEffect(() => {
    handleSearchPatient();
  }, [ipdInputs]);

  const handleSearchPatient = async () => {
    setIpdPatientDataObj({ loading: true });
    try {
      const query = {};
      const lowercasedInput = ipdInputs.toLowerCase().trim();
      if (lowercasedInput) {
        query.uhid = lowercasedInput;
      }
      const response = await request('/retail-sale-ipd-credit-list', { method: 'GET', query: query });
      if (response.success) {
        setIpdPatientDataObj({ loading: false, data: response?.data });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setIpdPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('Error fetching data', { variant: 'error' });
      setIpdPatientDataObj({ loading: false });
    }
  };

  useEffect(() => {
    handleSearchOpdPatient();
  }, [opdInputs]);

  const handleSearchOpdPatient = async () => {
    setOpdPatientDataObj({ loading: true });
    try {
      const query = {};
      const lowercasedInput = opdInputs.toLowerCase().trim();
      if (lowercasedInput) {
        query.uhid = lowercasedInput;
      }
      const response = await request('/retail-sale-opd-credit-list', { method: 'GET', query: query });
      if (response.success) {
        setOpdPatientDataObj({ loading: false, data: response?.data });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setOpdPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('Error fetching data', { variant: 'error' });
      setOpdPatientDataObj({ loading: false });
    }
  };

  const Type = watch('type');

  
  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none', width: '100%', height: 'auto' }} >
        <Container
          sx={{ mt: 2, p: 0.2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, width: '100%',
            maxWidth: 'none', minWidth: '100%', boxSizing: 'border-box' }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Expense Entry
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    setServiceDetails([]);
                    setLabDetails([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={handleQueryWindow}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Grid container spacing={2} >
              <Grid item xs={11} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Stack
                  sx={{
                    border: '1px solid #d3d3d3',
                    padding: '5px',
                    borderRadius: '5px',
                    justifyContent: 'space-between',
                    padding: '15px',
                    gap: 5
                  }}
                  spacing={0.5}
                  direction="row"
                >
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Voucher No.
                      </Typography>
                      <TextField sx={{ flex: 0.8 }} name="voucher_no"
                      disabled={!['new', 'edit'].includes(currentAction)} />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Expense Head
                      </Typography>
                      <SelectTag
                        disabled={!['new', 'edit'].includes(currentAction)}
                        formControlSX={{ flex: 0.8 }}
                        name="receipt_mode"
                        placeholder=""
                        menus={RECEIPT_MODE_INV}
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Pay Mode
                      </Typography>
                      <TextField sx={{ flex: 0.8 }} name="pay_mode"
                      disabled={!['new', 'edit'].includes(currentAction)} />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Amount
                      </Typography>
                      <TextField
                        inputProps={{ style: { fontSize: '1.5rem', padding: '5px' } }}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 0.8 }}
                        name="rec_amt"
                        placeholder="Enter Amount"
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Date
                      </Typography>
                      <TextField
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 0.8 }} name="date" type="date"
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Account
                      </Typography>
                      <SelectTag
                        formControlSX={{ flex: 0.8 }}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        name="ledger"
                        placeholder=""
                        menus={accountLedger.map((data) => ({
                          ...data,
                          name: data.ledger_name
                        }))}
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                    Remarks
                  </Typography>
                  <TextField
                    inputProps={{ style: { fontSize: '1.5rem', padding: '5px' } }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.8 }}
                    name="remarks"
                    placeholder="Remarks"
                  />
                </Stack>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
